/* StandardsRow — discrete pill set citing the published codes that
 * back the rule library, with hover tooltips that explain each one.
 *
 * Replaces the long prose paragraph that read "Spatial · budget · …
 * many cite a published code (NBC 2016, ECBC 2017, …)" — a wall of
 * names doesn't tell a designer what they actually are.
 *
 * The list is hand-curated here (small, stable, ~7 items) because
 * each citation's *purpose* and *jurisdiction* is editorial copy —
 * unlike a dataset registry. If a new code is added, append to
 * `STANDARDS` and a chip appears.
 */

import { useState } from "react";
import { ShieldCheck, Info } from "lucide-react";

interface Standard {
  id: string;
  label: string;
  badge: "statutory" | "guideline" | "convention";
  jurisdiction: string;
  scope: string;
  publisher: string;
}

const STANDARDS: ReadonlyArray<Standard> = [
  { id: "nbc-2016", label: "NBC 2016",     badge: "statutory",  jurisdiction: "India",       scope: "Part 3 (development control) + Part 4 (fire & life safety) + Part 9 (plumbing). Drives habitable-room minimums, staircase widths, egress.", publisher: "Bureau of Indian Standards" },
  { id: "ecbc-2017", label: "ECBC 2017",   badge: "statutory",  jurisdiction: "India",       scope: "Energy Conservation Building Code — envelope SHGC, lighting power density per space type, HVAC efficiency.", publisher: "Bureau of Energy Efficiency" },
  { id: "rera-2016", label: "RERA 2016",   badge: "statutory",  jurisdiction: "India",       scope: "Real Estate Regulation Act §3 — project registration threshold (≥ 500 m² or > 8 units). State authorities can be stricter.", publisher: "Government of India" },
  { id: "igbc",      label: "IGBC",        badge: "guideline",  jurisdiction: "India",       scope: "Indian Green Building Council ratings — water + energy credits. Often mandated by state-level by-laws.", publisher: "IGBC" },
  { id: "griha",     label: "GRIHA",       badge: "guideline",  jurisdiction: "India",       scope: "Green Rating for Integrated Habitat Assessment. Overlaps IGBC; some cities mandate at single-star.", publisher: "TERI / MoEFCC" },
  { id: "nkba",      label: "NKBA",        badge: "convention", jurisdiction: "International", scope: "Planning Guidelines — kitchen work-triangle, counter clearances, ergonomic baselines.", publisher: "National Kitchen & Bath Association" },
  { id: "ansi-117",  label: "ANSI A117.1", badge: "guideline",  jurisdiction: "USA · cross-reference", scope: "Accessible & usable buildings — wheelchair turning radius (1500 mm), door clear widths. NBC 2016 Part 3 universal-design section aligns.", publisher: "American National Standards Institute" },
];

const BADGE_CLASS: Record<Standard["badge"], string> = {
  statutory:  "bg-foyr/10 text-foyr-deep border-foyr/30",
  guideline:  "bg-sea/10 text-sea border-sea/30",
  convention: "bg-cream-dark text-ink-700 border-border",
};
const BADGE_LABEL: Record<Standard["badge"], string> = {
  statutory:  "code",
  guideline:  "guideline",
  convention: "convention",
};

export function StandardsRow(): JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="panel p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-3.5 h-3.5 text-ink-500" />
        <span className="eyebrow">Backed by</span>
        <span className="text-[10px] text-ink-500">— hover any pill for what it covers</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {STANDARDS.map((s) => (
          <button
            key={s.id}
            type="button"
            onMouseEnter={() => setOpen(s.id)}
            onMouseLeave={() => setOpen((cur) => (cur === s.id ? null : cur))}
            onFocus={() => setOpen(s.id)}
            onBlur={() => setOpen((cur) => (cur === s.id ? null : cur))}
            onClick={() => setOpen((cur) => (cur === s.id ? null : s.id))}
            className={`pill border tabular text-[12px] hover:translate-y-[-1px] transition-all ${BADGE_CLASS[s.badge]}`}
            aria-expanded={open === s.id}
            title={`${s.label} — ${s.publisher} (${s.jurisdiction})`}
          >
            {s.label}
            <span className="ml-1.5 text-[9px] uppercase tracking-wider opacity-70">{BADGE_LABEL[s.badge]}</span>
          </button>
        ))}
      </div>
      {open ? (
        <div className="mt-3 panel-subtle p-3 text-[12px] text-ink-700 leading-snug flex gap-2">
          <Info className="w-3.5 h-3.5 text-ink-500 shrink-0 mt-0.5" />
          <div>
            <div className="text-ink-950 font-semibold">{STANDARDS.find((s) => s.id === open)?.label}</div>
            <div className="text-[11px] text-ink-500">
              {STANDARDS.find((s) => s.id === open)?.publisher} · {STANDARDS.find((s) => s.id === open)?.jurisdiction}
            </div>
            <p className="mt-1.5">{STANDARDS.find((s) => s.id === open)?.scope}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
